import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Home, 
  ShoppingBag, 
  HelpCircle, 
  ShieldCheck, 
  FileText, 
  LayoutDashboard,
  ArrowRight,
  Newspaper,
  BookOpen,
  Layers,
  Download,
  ExternalLink
} from 'lucide-react';
import { motion } from 'framer-motion';
import { db, collection, getDocs, query, where, orderBy } from '@/firebase';
import { Button } from '@/components/ui/button';

interface Article {
  id: string;
  title: string;
  slug: string;
  category: string;
}

interface Product {
  id: string;
  name: string;
  slug?: string;
  price?: number;
  category?: string;
}

const SitemapSection = ({ 
  title, 
  icon: Icon, 
  badge,
  links 
}: { 
  title: string; 
  icon: any; 
  badge?: string;
  links: { name: string; path: string; desc?: string }[];
}) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="bg-card rounded-3xl p-6 sm:p-8 border border-border shadow-xl shadow-foreground/5 h-full flex flex-col justify-between"
  >
    <div>
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3.5">
          <div className="w-11 h-11 rounded-2xl bg-gold/10 flex items-center justify-center text-primary shrink-0">
            <Icon className="w-5 h-5" />
          </div>
          <h2 className="text-lg sm:text-xl font-black text-foreground">{title}</h2>
        </div>
        {badge && (
          <span className="px-3 py-1 rounded-full text-xs font-black bg-gold/15 text-gold border border-gold/30">
            {badge}
          </span>
        )}
      </div>
      
      <ul className="space-y-4">
        {links.map((link) => (
          <li key={link.path}>
            <Link to={link.path} className="group block">
              <div className="flex items-center justify-between mb-1 gap-2">
                <span className="font-bold text-sm text-foreground group-hover:text-primary transition-colors line-clamp-1">
                  {link.name}
                </span>
                <ArrowRight className="w-3.5 h-3.5 text-primary opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all flex-shrink-0" />
              </div>
              {link.desc && (
                <p className="text-xs text-foreground/50 font-medium line-clamp-1">
                  {link.desc}
                </p>
              )}
            </Link>
          </li>
        ))}
        {links.length === 0 && (
          <p className="text-xs text-foreground/40 italic">Memuat konten...</p>
        )}
      </ul>
    </div>
  </motion.div>
);

export default function Sitemap() {
  const [articles, setArticles] = React.useState<Article[]>([]);
  const [products, setProducts] = React.useState<Product[]>([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    async function fetchData() {
      try {
        // Fetch Articles
        const qArticles = query(
          collection(db, 'articles'),
          where('status', '==', 'published'),
          orderBy('createdAt', 'desc')
        );
        const articlesSnap = await getDocs(qArticles);
        const articlesData = articlesSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Article));
        setArticles(articlesData);

        // Fetch Products
        const qProducts = query(
          collection(db, 'products'),
          orderBy('createdAt', 'desc')
        );
        const productsSnap = await getDocs(qProducts);
        const productsData = productsSnap.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as Product));
        setProducts(productsData);
      } catch (error) {
        console.error("Error fetching sitemap data:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  const serviceLinks = [
    { name: 'Sertifikat Badan Usaha (SBU)', path: '/services/sbu', desc: 'Pengurusan legalitas & sertifikasi badan usaha jasa konstruksi.' },
    { name: 'Sertifikat Kompetensi Kerja (SKK)', path: '/services/skk', desc: 'Sertifikasi kompetensi tenaga ahli dan terampil konstruksi.' },
    { name: 'NIB OSS RBA Konstruksi', path: '/services/nib-oss', desc: 'Pembuatan dan pemutakhiran perizinan berusaha berbasis risiko.' },
    { name: 'Konsultasi Perizinan', path: '/services/konsultasi-perizinan', desc: 'Pendampingan regulasi tender dan kepatuhan hukum.' },
    { name: 'Pendampingan Proyek', path: '/services/pendampingan-proyek', desc: 'Asistensi teknis dan supervisi manajemen proyek lapangan.' },
    { name: 'Penyusunan Dokumen Penawaran', path: '/services/penyusunan-dokumen-penawaran', desc: 'Penyusunan dokumen teknis, administrasi, dan harga tender.' },
    { name: 'Pajak Badan & Pribadi Konstruksi', path: '/services/pajak-badan-pribadi', desc: 'Laporan keuangan dan kepatuhan pajak jasa konstruksi.' },
    { name: 'SPT Masa & e-Faktur', path: '/services/spt-masa-efaktur', desc: 'Pelaporan PPN dan PPh final konstruksi.' },
    { name: 'Edukasi & Pelatihan Konstruksi', path: '/services/edukasi-pelatihan', desc: 'Bimbingan teknis estimasi, SMKK, dan software teknik.' }
  ];

  const educationLinks = [
    { name: 'Dasar-Dasar K3 Konstruksi', path: '/education/dasar-dasar-k3-konstruksi', desc: 'Prinsip implementasi keselamatan dan kesehatan kerja.' },
    { name: 'Manajemen Kontrak Jasa Konstruksi', path: '/education/manajemen-kontrak-jasa-konstruksi', desc: 'Klausul baku, addendum, dan mitigasi sengketa tender.' },
    { name: 'Standar SNI Struktur Beton', path: '/education/standar-sni-struktur-beton', desc: 'Pedoman mutu pembetonan dan pengujian lab teknis.' },
    { name: 'Estimasi Biaya dan Penjadwalan (RAB)', path: '/education/estimasi-biaya-dan-penjadwalan', desc: 'Penyusunan AHSP, kurva S, dan cash flow proyek.' },
    { name: 'Teknologi BIM Konstruksi', path: '/education/teknologi-bim-konstruksi', desc: 'Penerapan Building Information Modeling modern.' },
    { name: 'Metode Kerja & Shop Drawing', path: '/education/metode-kerja-shop-drawing', desc: 'Standar gambar kerja detail dan instruksi lapangan.' },
    { name: 'Audit Mutu & Quality Assurance (RMPK)', path: '/education/audit-mutu-quality-assurance', desc: 'Pengendalian inspeksi mutu material dan pekerjaan.' },
    { name: 'Sistem Pengadaan Elektronik (SPSE)', path: '/education/sistem-pengadaan-elektronik-spse', desc: 'Tata cara upload penawaran e-Tendering LPSE.' },
    { name: 'E-Katalog Sektor Pemerintah', path: '/education/sistem-ekatalog-government', desc: 'Pendaftaran etalase dan transaksi e-Purchasing.' },
    { name: 'SIKaP LKPP Penyedia', path: '/education/sikap-lkpp-penyedia', desc: 'Integrasi kualifikasi dan verifikasi data penyedia.' },
    { name: 'Etika Pengadaan Barang & Jasa', path: '/education/etik-pengadaan-barang-jasa', desc: 'Integritas dan kepatuhan PBJ pemerintah.' }
  ];

  return (
    <div className="pt-32 pb-24 bg-background min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-14 flex flex-col md:flex-row md:items-end justify-between gap-6 pb-8 border-b border-border">
          <div>
            <span className="text-primary font-black uppercase tracking-[0.4em] text-xs mb-3 block">Direktori Struktur Situs</span>
            <h1 className="text-4xl md:text-6xl font-black text-foreground tracking-tight mb-4">
              Site<span className="text-primary">map.</span>
            </h1>
            <p className="text-foreground/60 max-w-2xl text-base font-medium">
              Daftar komprehensif seluruh halaman, layanan, modul edukasi, artikel, dan katalog produk digital AZMAN 204.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <a 
              href="/sitemap.xml" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-2xl bg-gold/15 text-gold border border-gold/30 hover:bg-gold/25 font-bold text-xs transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              <span>Buka XML Sitemap (Googlebot)</span>
            </a>
          </div>
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Main Navigation */}
          <SitemapSection 
            title="Navigasi Inti" 
            icon={Home}
            links={[
              { name: 'Beranda Utama', path: '/', desc: 'Halaman utama dengan rangkuman solusi konstruksi.' },
              { name: 'Layanan Konstruksi', path: '/services', desc: 'Katalog jasa legalitas, teknis, & perizinan.' },
              { name: 'Produk Dokumen Digital', path: '/products', desc: 'Template format dokumen RAB, RKK, SMKK, & software.' },
              { name: 'Wawasan & Artikel', path: '/articles', desc: 'Kumpulan artikel tender dan berita konstruksi.' },
              { name: 'Regulasi Konstruksi', path: '/regulations', desc: 'Kompilasi peraturan Menteri PUPR & LPSE.' },
              { name: 'Pusat Edukasi', path: '/education', desc: 'Modul pelatihan standar teknik konstruksi.' },
              { name: 'Pusat Bantuan', path: '/help', desc: 'Panduan pengguna dan FAQ lengkap.' }
            ]}
          />

          {/* Dynamic Products Catalog */}
          <SitemapSection 
            title="Katalog Produk Digital" 
            icon={ShoppingBag}
            badge={`${products.length} Produk`}
            links={loading ? [] : products.map(product => ({
              name: product.name,
              path: `/product/${product.slug || product.id}`,
              desc: product.price ? `Rp ${product.price.toLocaleString('id-ID')}` : 'Template Dokumen'
            }))}
          />

          {/* Dynamic Articles Section */}
          <SitemapSection 
            title="Artikel & Berita" 
            icon={Newspaper}
            badge={`${articles.length} Artikel`}
            links={loading ? [] : articles.map(article => ({
              name: article.title,
              path: `/article/${article.slug || article.id}`,
              desc: `Kategori: ${article.category || 'Konstruksi'}`
            }))}
          />

          {/* Services Section */}
          <SitemapSection 
            title="Layanan & Perizinan" 
            icon={Layers}
            badge="9 Layanan"
            links={serviceLinks}
          />

          {/* Education Modules */}
          <SitemapSection 
            title="Modul Edukasi Standar" 
            icon={BookOpen}
            badge="11 Modul"
            links={educationLinks}
          />

          {/* User Experience & Legal */}
          <SitemapSection 
            title="Portal Akun & Legalitas" 
            icon={ShieldCheck}
            links={[
              { name: 'Dashboard Pengguna', path: '/dashboard', desc: 'Pantau transaksi & riwayat unduh aset digital.' },
              { name: 'Pusat Unduhan (Downloads)', path: '/dashboard?tab=downloads', desc: 'Akses instan berkas template yang telah dibeli.' },
              { name: 'Status Pemesanan', path: '/dashboard?tab=orders', desc: 'Cek verifikasi dan bukti transfer pembayaran.' },
              { name: 'Syarat & Ketentuan', path: '/terms', desc: 'Ketentuan pemesanan & lisensi pemakaian dokumen.' },
              { name: 'Kebijakan Privasi', path: '/privacy', desc: 'Perlindungan kerahasiaan data pengguna.' }
            ]}
          />
        </div>

        {/* Footer Info Box */}
        <div className="mt-16 p-8 sm:p-12 rounded-[2.5rem] bg-navy text-white relative overflow-hidden border border-gold/30">
          <div className="absolute top-0 right-0 w-80 h-80 bg-gold/10 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none" />
          <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="max-w-xl text-center md:text-left">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold/20 text-gold text-xs font-black uppercase mb-3">
                Terverifikasi Google Indexing
              </span>
              <h3 className="text-2xl sm:text-3xl font-black mb-3 text-white">Semua Halaman Siap Dirayapi Mesin Pencari</h3>
              <p className="text-white/70 text-sm font-medium leading-relaxed">
                Seluruh artikel berita, template dokumen, dan modul edukasi telah disinkronkan ke dalam berkas XML Sitemap dan HTML Sitemap untuk mempermudah indeks Google.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto">
              <Button asChild className="bg-gold hover:bg-gold-light text-navy font-black px-6 py-6 rounded-2xl">
                <a href="/sitemap.xml" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Lihat File XML
                </a>
              </Button>
              <Button asChild variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-2xl px-6 py-6 font-bold">
                <Link to="/help">Pusat Bantuan</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
